import { createSelector } from 'reselect';
import { selectors as configSelectors } from 'mage-config';
import _get from 'lodash/get';
import { LEAD_ONBOARDING_TYPES, SEGMENT } from '@/constants';
import { DEFAULT_SENSITIVE_GOODS_VALUE } from '@/containers/SensitiveGoods/constants';
import { appState, stripQueryParameters } from './utils';
import { selectQueryFromLocation } from './widget';
import {
  getCountryConfig,
  getSignupCountries,
  getSalesTeamInfos,
  getSupportedCountries,
  getCountries,
} from './options';

const _data = createSelector(appState, ({ data }) => data || {});

// This is a temporary selector that we will remove once we refactor the app
export const getLoadingAndDisabled = createSelector(
  _data,
  ({ isLoading = false, isDisabled = false }) => ({ isLoading, isDisabled }),
);

export const getSelectedCountryKey = createSelector(_data, ({ data = {} }) => {
  return (data.country || '').toUpperCase();
});

export const getSelectedCountry = createSelector(
  getSelectedCountryKey,
  getSignupCountries,
  (key, countries = []) => countries.find(c => c.key === key),
);

export const getSelectedCountryCurrency = createSelector(
  getSelectedCountryKey,
  getCountries,
  (key, countries = []) =>
    _get(countries.find(c => c.key === key) || {}, 'data.currencies[0]', null),
);

export const getSelectedPlatformKey = createSelector(_data, ({ data = {} }) => data.platform || '');

export const getSelectedPartnerKey = createSelector(_data, ({ data = {} }) => data.partner || '');

export const getSelectedAnnualRevenue = createSelector(
  _data,
  ({ data = {} }) => data.annualRevenue || '',
);

export const getCurrentRegion = createSelector(
  _data,
  selectQueryFromLocation,
  configSelectors.region, // The region here is the closest region to the user on global
  ({ data = {} }, query, fallbackRegion) => {
    return data.region || _get(query, 'region', null) || fallbackRegion;
  },
);

export const getRegionOfSignupCountry = createSelector(
  _data,
  getSupportedCountries,
  ({ data: { country } = {} }, supportedCountries = []) =>
    (supportedCountries.find(({ key }) => key === country) || {}).region,
);

export const getSetupData = createSelector(
  _data,
  getSelectedCountryKey,
  getCurrentRegion,
  ({ data: { annualRevenue, phone: phoneNumber, ...data } = {} }, country) => ({
    ...data,
    annualRevenue,
    phoneNumber,
    country,
    formSubmissionUrl: stripQueryParameters(window.location.href),
    leadOnboardingType: LEAD_ONBOARDING_TYPES.AUTOMATIC, // For automatic onboarding
  }),
);

export const getUserData = createSelector(
  getSetupData,
  ({ firstName = '', lastName = '', email = '', phoneNumber: phone = '', website = '' }) => ({
    firstName,
    lastName,
    email,
    phone,
    website,
  }),
);

export const sellsSensitiveGoods = createSelector(getSetupData, ({ sellsSensitiveGoods = null }) =>
  sellsSensitiveGoods
    ? sellsSensitiveGoods !== DEFAULT_SENSITIVE_GOODS_VALUE
    : !!sellsSensitiveGoods,
);

export const getMerchantSensitiveGood = createSelector(
  getSetupData,
  ({ sellsSensitiveGoods = null }) => sellsSensitiveGoods,
);

export const getFriendbuyReferralCode = createSelector(
  getSetupData,
  ({ utmFriendbuy = null }) => utmFriendbuy,
);

export const getSalesTeamInfo = createSelector(
  getSelectedCountryKey,
  getSalesTeamInfos,
  (countryKey, salesTeamInfos = []) => salesTeamInfos.find(i => i.countryKey === countryKey),
);

export const getShopDirectoryUrl = createSelector(
  getCurrentRegion,
  region =>
    ({
      US: 'https://www.klarna.com/us/shop-directory/',
      EU: 'http://klarna.com',
    }[region.toUpperCase()]),
);

export const getProducts = createSelector(_data, ({ data = {} }) => data.products || []);

export const getCreatedMerchantId = createSelector(_data, ({ data = {} }) => data.merchantId);

export const getSetupError = createSelector(_data, ({ setupError }) => setupError);

export const getLeadData = createSelector(
  _data,
  getSelectedCountryKey,
  getCurrentRegion,
  ({ data: { annualRevenue, phone: phoneNumber, ...data } = {} }, country) => ({
    ...data,
    phoneNumber,
    country,
    annualRevenue,
  }),
);

export const isExistingUser = createSelector(getSetupData, ({ isExistingUser }) => isExistingUser);

export const hasFailedSetup = createSelector(getSetupData, ({ isError }) => isError);

export const getWidgetUrl = createSelector(getSetupData, ({ widgetUrl }) => widgetUrl);

export const getVolumeSeparationConfig = createSelector(
  getCountryConfig,
  getSetupData,
  getSelectedCountryCurrency,
  (countryConfig = {}, { country }, currency) =>
    countryConfig[country]?.VOLUME_SEPARATION
      ? { ...countryConfig[country]?.VOLUME_SEPARATION, currency }
      : { ...countryConfig.US.VOLUME_SEPARATION, currency: 'USD' },
);

export const getSegment = createSelector(
  getVolumeSeparationConfig,
  getSetupData,
  ({ medium, large } = {}, { annualRevenue } = {}) => {
    if (!medium || !large || !annualRevenue) {
      return undefined;
    } else if (annualRevenue <= medium) {
      return SEGMENT.SMALL_BUSINESS;
    } else if (annualRevenue >= large) {
      return SEGMENT.LARGE_BUSINESS;
    } else {
      return SEGMENT.MEDIUM_BUSINESS;
    }
  },
);

export const shouldGoThroughAutomaticOnboarding = createSelector(
  getVolumeSeparationConfig,
  getSegment,
  ({ AO = [] } = {}, segment) => !!segment && AO.includes(segment),
);

export const hasAboveThresholdVolume = createSelector(
  getSegment,
  segment => segment === SEGMENT.LARGE_BUSINESS,
);

export const shouldShowContactSalesButton = createSelector(getSegment, () => false);

/* Temporal for Kustom */
export const isKustomPartner = createSelector(_data, () => {
  const searchParams = new URLSearchParams(location.search);
  const productParam = searchParams.get('partner') || 'default';

  return productParam === 'kustom';
});
